/**
 * CartTableItemQuantityItem Component
 *
 * This component renders a quantity selector for a single cart item in a table.
 * It allows the user to increase or decrease the quantity of the item, supporting
 * continuous increment/decrement when holding the buttons. It also handles edge cases
 * such as maximum quantity limits and removing an item when quantity reaches zero.
 *
 * Features:
 * - Shows current item quantity.
 * - Increment/decrement buttons with continuous hold support.
 * - Prevents exceeding MAX_CART_QUANTITY.
 * - Removes item if quantity reaches 1 and decrement is clicked.
 * - Supports different item types: product, package, and offer.
 * - Uses Redux actions `addToCart` and `decreaseQuantity` to update global state.
 * - Supports multi-language display using `i18n`.
 *
 * @component
 *
 * @param {Object} TableRow - Object representing the cart item.
 * @param {number} TableRow.productId - Product ID (if item is a product).
 * @param {number} TableRow.offerId - Offer ID (if item is an offer).
 * @param {number} TableRow.packageId - Package ID (if item is a package).
 * @param {string} TableRow.itemType - Type of item: "product", "offer", or "package".
 * @param {string} TableRow.name - Item name (default language).
 * @param {string} TableRow.nameAr - Item name in Arabic.
 * @param {string} TableRow.nameEn - Item name in English.
 * @param {number} TableRow.price - Item price.
 * @param {number} TableRow.quantity - Current quantity of the item in the cart.
 * @param {string} TableRow.image - Item image URL or path.
 * @param {Object} TableRow.currency - Currency object for the item.
 * @param {Object} TableRow.warehouse - Warehouse object for the item.
 *
 * @returns {JSX.Element} A React component displaying the quantity input with increment/decrement buttons.
 *
 * @example
 * <CartTableItemQuantityItem TableRow={cartItem} />
 */
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import {
  addToCart,
  decreaseQuantity,
  removeFromCart,
  toggleIsSharedCartStatus,
} from "../../redux/slices/cartSlice";
import {
  ArabicCode,
  BASE_LOCAL_IMAGES_DIR,
  CardCounterSpeed,
  MAX_CART_QUANTITY,
} from "../../utils/constants/CommonConstants";
import {
  cart_trash_IMG,
  minus_button_IMG,
  white_plus_IMG,
} from "../../utils/constants/Images";

interface CartTableItemQuantityItemProps {
  TableRow: any;
}

const CartTableItemQuantityItem: React.FC<CartTableItemQuantityItemProps> = ({
  TableRow,
}) => {
  const dispatch = useDispatch();
  const { i18n } = useTranslation();

  const uniqueKey = TableRow.productId
    ? `product-${TableRow.productId}`
    : TableRow.offerId
      ? `offer-${TableRow.offerId}`
      : `package-${TableRow.packageId}`;

  const [itemQuantities, setItemQuantities] = useState<Record<string, number>>(
    {},
  );
  const [isIncreasing, setIsIncreasing] = useState<Record<string, boolean>>({});
  const [isDecreasing, setIsDecreasing] = useState<Record<string, boolean>>({});

  const handleAddItemToCart = () => {
    const currentQty = itemQuantities[uniqueKey] || 0;
    if (currentQty >= MAX_CART_QUANTITY) return;

    dispatch(
      addToCart({
        productId: TableRow.productId || 0,
        offerId: TableRow.offerId || 0,
        packageId: TableRow.packageId || 0,
        quantity: 1,
        price: TableRow.price,
        image: TableRow.image,
        name: TableRow.name,
        nameAr: TableRow.nameAr,
        nameEn: TableRow.nameEn,
        warehouse: TableRow.warehouse,
        itemType: TableRow.itemType,
        currency: TableRow.currency,
      }),
    );
    dispatch(toggleIsSharedCartStatus(false));
  };

  const handleRemoveItem = () => {
    const currentQty = itemQuantities[uniqueKey] || 0;
    if (currentQty === 1) {
      dispatch(
        removeFromCart({
          productId: TableRow.productId,
          offerId: TableRow.offerId,
          packageId: TableRow.packageId,
          warehouse: TableRow.warehouse,
        }),
      );
      dispatch(toggleIsSharedCartStatus(false));
    } else if (currentQty > 1) {
      dispatch(
        decreaseQuantity({
          productId: TableRow.productId,
          offerId: TableRow.offerId,
          packageId: TableRow.packageId,
          quantity: 1,
          warehouse: TableRow.warehouse,
        }),
      );
      dispatch(toggleIsSharedCartStatus(false));
    }
  };

  const handleQuantityChange = (value: string) => {
    const currentQty = itemQuantities[uniqueKey] || 1;
    let parsed = Math.floor(Number(value));
    if (isNaN(parsed) || parsed < 1) parsed = 1;
    if (parsed > MAX_CART_QUANTITY) parsed = MAX_CART_QUANTITY;

    setItemQuantities((prev) => ({ ...prev, [uniqueKey]: parsed }));
    const diff = parsed - currentQty;

    if (diff > 0) {
      dispatch(
        addToCart({
          productId: TableRow.productId || 0,
          offerId: TableRow.offerId || 0,
          packageId: TableRow.packageId || 0,
          quantity: diff,
          price: TableRow.price,
          image: TableRow.image,
          name: TableRow.name,
          nameAr: TableRow.nameAr,
          nameEn: TableRow.nameEn,
          warehouse: TableRow.warehouse,
          itemType: TableRow.itemType,
          currency: TableRow.currency,
        }),
      );

      dispatch(toggleIsSharedCartStatus(false));
    } else if (diff < 0) {
      dispatch(
        decreaseQuantity({
          productId: TableRow.productId,
          offerId: TableRow.offerId,
          packageId: TableRow.packageId,
          quantity: Math.abs(diff),
          warehouse: TableRow.warehouse,
        }),
      );
      dispatch(toggleIsSharedCartStatus(false));
    }
  };

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (isIncreasing[uniqueKey]) {
      const increment = () => {
        const currentQty = itemQuantities[uniqueKey] || 0;
        if (currentQty >= MAX_CART_QUANTITY) return;
        setItemQuantities((prev) => ({ ...prev, [uniqueKey]: currentQty + 1 }));
        handleAddItemToCart();
        timeout = setTimeout(increment, CardCounterSpeed);
      };
      timeout = setTimeout(increment, CardCounterSpeed);
    }
    return () => clearTimeout(timeout);
  }, [isIncreasing[uniqueKey], itemQuantities[uniqueKey]]);

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (isDecreasing[uniqueKey]) {
      const decrement = () => {
        setItemQuantities((prev) => ({
          ...prev,
          [uniqueKey]: Math.max((prev[uniqueKey] || 0) - 1, 0),
        }));
        handleRemoveItem();
        timeout = setTimeout(decrement, CardCounterSpeed);
      };
      decrement();
    }
    return () => clearTimeout(timeout);
  }, [isDecreasing[uniqueKey]]);

  useEffect(() => {
    setItemQuantities((prev) => ({ ...prev, [uniqueKey]: TableRow.quantity }));
  }, [TableRow.quantity, uniqueKey]);

  return (
    <div className="flex items-center gap-0">
      <button
        onMouseDown={() =>
          setIsDecreasing((prev) => ({ ...prev, [uniqueKey]: true }))
        }
        onMouseUp={() =>
          setIsDecreasing((prev) => ({ ...prev, [uniqueKey]: false }))
        }
        onMouseLeave={() =>
          setIsDecreasing((prev) => ({ ...prev, [uniqueKey]: false }))
        }
        className={`flex h-8 w-8 items-center justify-center bg-componentFilterOutline ${
          i18n.language === ArabicCode ? "rounded-r-md" : "rounded-l-md"
        }`}
        disabled={itemQuantities[uniqueKey] <= 0}
      >
        <img
          src={`${BASE_LOCAL_IMAGES_DIR}/${
            itemQuantities[uniqueKey] === 1 ? cart_trash_IMG : minus_button_IMG
          }`}
          alt="minus"
        />
      </button>

      <input
        type="number"
        min={1}
        max={MAX_CART_QUANTITY}
        className="h-8 w-full bg-primaryLighter text-center outline-none"
        value={
          typeof itemQuantities[uniqueKey] === "number"
            ? Math.min(
                Math.max(1, Math.floor(itemQuantities[uniqueKey])),
                MAX_CART_QUANTITY,
              )
            : 1
        }
        onKeyDown={(e) => {
          if ([".", ",", "e"].includes(e.key)) e.preventDefault();
        }}
        onChange={(e) => handleQuantityChange(e.target.value)}
      />

      <button
        onMouseDown={() =>
          setIsIncreasing((prev) => ({ ...prev, [uniqueKey]: true }))
        }
        onMouseUp={() =>
          setIsIncreasing((prev) => ({ ...prev, [uniqueKey]: false }))
        }
        onMouseLeave={() =>
          setIsIncreasing((prev) => ({ ...prev, [uniqueKey]: false }))
        }
        onClick={handleAddItemToCart}
        className={`flex h-8 w-8 items-center justify-center bg-primaryMain ${
          i18n.language === ArabicCode ? "rounded-l-md" : "rounded-r-md"
        }`}
      >
        <img src={`${BASE_LOCAL_IMAGES_DIR}/${white_plus_IMG}`} alt="plus" />
      </button>
    </div>
  );
};

export default CartTableItemQuantityItem;
